import { useState } from "react";
import IconButton from "@/components/IconButton";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { useMobilePhotoDownload } from "@/hooks/useMobilePhotoDownload";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

export interface Photo {
  id: string;
  url: string;
  filename?: string;
  uploadedAt?: string;
}

interface PhotoGalleryProps {
  photos: Photo[];
  onDelete?: (photoId: string) => Promise<void>;
  onDownload?: (photo: Photo) => Promise<void>;
  className?: string;
  showActions?: boolean;
}

export default function PhotoGallery({
  photos,
  onDelete,
  onDownload,
  className = "",
  showActions = true
}: PhotoGalleryProps) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const { downloadPhoto, isMobile } = useMobilePhotoDownload();

  if (!photos || photos.length === 0) {
    return null;
  }

  const selectedPhoto = photos[selectedPhotoIndex];

  const handleDeleteClick = (photo: Photo) => {
    setPhotoToDelete(photo);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!photoToDelete || !onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete(photoToDelete.id);
      setShowDeleteDialog(false);
      setPhotoToDelete(null);

      // Adjust selected index if we're deleting the currently selected photo
      if (selectedPhotoIndex >= photos.length - 1) {
        setSelectedPhotoIndex(Math.max(0, photos.length - 2));
      }
    } catch (error) {
      console.error("Failed to delete photo:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownload = async (photo: Photo) => {
    if (onDownload) {
      try {
        await onDownload(photo);
      } catch (error) {
        console.error("Download failed:", error);
        showErrorSnackbar("Download failed. Please try again.");
      }
    } else {
      // Use mobile-specific download functionality
      await downloadPhoto({
        photo,
        onSuccess: () => {
          if (isMobile) {
            showSnackbar("Photo opened for saving. Long-press to save to Photos.", {
              variant: "info"
            });
          } else {
            showSnackbar("Photo downloaded");
          }
        },
        onError: (error) => {
          showErrorSnackbar(`Download failed: ${error}`);
        }
      });
    }
  };

  // Long-press functionality for mobile
  const handleTouchStart = (photo: Photo) => {
    if (!isMobile) return;

    const timer = setTimeout(() => {
      handleDownload(photo);
    }, 800); // 800ms for long-press
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleTouchMove = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Gallery Header */}
      <div className="flex justify-start items-center mb-4">
        <div className="text-body-medium text-sys-on-surface">
          {photos.length} photo{photos.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Gallery Container */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Thumbnails Column */}
        <div className="flex sm:flex-col gap-2 order-2 sm:order-1">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className={`relative cursor-pointer group ${
                index === selectedPhotoIndex
                  ? "ring-2 ring-sys-brand-primary rounded"
                  : "ring-1 ring-sys-brand-surface-dim"
              }`}
              onClick={() => setSelectedPhotoIndex(index)}
            >
              <img
                src={photo.url}
                alt={`Photo ${index + 1}`}
                className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded"
              />

              {/* Selection indicator */}
              {index === selectedPhotoIndex && (
                <div className="absolute left-0 top-0 bottom-0 w-1 rounded bg-sys-brand-primary"></div>
              )}
            </div>
          ))}
        </div>

        {/* Main Image */}
        <div className="flex-1 order-1 sm:order-2">
          <div className="relative">
            <img
              src={selectedPhoto.url}
              alt={`Selected photo ${selectedPhotoIndex + 1}`}
              className="w-full h-auto object-cover rounded-lg"
              onTouchStart={() => handleTouchStart(selectedPhoto)}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
            />

            {/* Main image actions */}
            {showActions && (
              <div className="absolute top-4 right-4 flex">
                {onDownload && (
                  <IconButton name="download" variant="filledTonal" onClick={() => handleDownload(selectedPhoto)} />
                )}
                {onDelete && (
                  <IconButton name="delete" variant="filledTonal" onClick={() => handleDeleteClick(selectedPhoto)} />
                )}
              </div>
            )}

            {/* Photo info overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-sys-brand-scrim/70 to-transparent p-4 rounded-b-lg">
              <div className="text-sys-brand-on-primary">
                <div className="text-sm opacity-90">
                  Photo {selectedPhotoIndex + 1} of {photos.length}
                </div>
                {selectedPhoto.filename && <div className="text-xs opacity-75 truncate">{selectedPhoto.filename}</div>}
                {isMobile && <div className="text-xs opacity-75 mt-1">Long-press to save to Photos</div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClosed={() => setShowDeleteDialog(false)}
        headline="Delete Photo"
        className="sm:max-w-[423px] p-6 pr-2"
        actions={
          <>
            <Button variant="text" onClick={() => setShowDeleteDialog(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              theme="error"
              icon={isDeleting ? "progress_activity" : undefined}
              iconClassName={isDeleting ? "animate-spin w-6 h-6 !block" : undefined}
            >
              {isDeleting ? "Deleting..." : "Delete Photo"}
            </Button>
          </>
        }
      >
        <p>Are you sure you want to delete this photo? This action cannot be undone.</p>
      </Dialog>
    </div>
  );
}
